import React, { useState, useRef, useEffect } from 'react'
import AsyncSelect from 'react-select/async'
import { Turbo } from '@hotwired/turbo-rails'

// Global search component for header
export default function GlobalSearch({ 
  searchUrl, 
  clientPathTemplate = '/my/clients/:id',
  fundPathTemplate = '/my/clients/:client_id/funds/:id',
  fundAssetPathTemplate = '/my/clients/:client_id/funds/:fund_id/fund_assets/:id',
  valuationPathTemplate = '/my/valuations/:id',
  placeholder = 'Search clients, funds, assets...',
  noResultsText = 'No results found',
  loadingText = 'Searching...',
  minCharsText = 'Type at least 2 characters'
}) {
  const [selectedValue, setSelectedValue] = useState(null)
  const [menuIsOpen, setMenuIsOpen] = useState(undefined)
  const selectRef = useRef(null)
  
  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const shortcutKey = isMac ? '⌘K' : 'Ctrl+K'
  
  // Add global keyboard shortcut (Ctrl/Cmd + K) to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        selectRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const loadOptions = async (inputValue) => {
    if ((inputValue || '').trim().length < 2) {
      return []
    }
    try {
      const response = await fetch(`${searchUrl}?q=${encodeURIComponent(inputValue)}`)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error loading search options:', error)
      return []
    }
  }

  const buildPath = (template, params) => {
    return Object.keys(params).reduce((acc, key) => {
      return acc.replace(`:${key}`, encodeURIComponent(params[key]))
    }, template)
  }

  const handleChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedValue(null)
      return
    }

    const { type, id, client_id, fund_id } = selectedOption

    let path
    // Build path based on type
    switch (type) {
      case 'client':
        path = buildPath(clientPathTemplate, { id })
        break
      case 'fund':
        path = buildPath(fundPathTemplate, { client_id, id })
        break
      case 'fund_asset':
        path = buildPath(fundAssetPathTemplate, { client_id, fund_id, id })
        break
      case 'valuation':
        path = buildPath(valuationPathTemplate, { id })
        break
      default:
        console.error('Unknown search result type:', type)
        return
    }

    // Clear the search field and remove focus before navigating
    setSelectedValue(null)
    if (selectRef.current) {
      selectRef.current.blur()
    }
    
    // Navigate using Turbo for smooth transitions
    Turbo.visit(path)
  }

  // Custom Placeholder component with keyboard shortcut hint
  const CustomPlaceholder = (props) => {
    return (
      <div className="react-select__placeholder-wrapper">
        <span className="react-select__placeholder-text">{placeholder}</span>
        <span className="react-select__shortcut-hint">{shortcutKey}</span>
      </div>
    )
  }
  
  // Custom Option component with Enter hotkey hint
  const CustomOption = (props) => {
    const { data, innerRef, innerProps, isFocused, isSelected } = props
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`react-select__option ${isFocused ? 'react-select__option--is-focused' : ''} ${isSelected ? 'react-select__option--is-selected' : ''}`}
      >
        <span className="react-select__option-label">{data.label}</span>
        {isFocused && <span className="hotkey react-select__option-hotkey">↵</span>}
      </div>
    )
  }

  // Styles are now handled by SCSS in _header.scss

  return (
    <div className="global-search">
      <AsyncSelect
        ref={selectRef}
        cacheOptions
        defaultOptions={false}
        value={selectedValue}
        loadOptions={loadOptions}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable
        isSearchable
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        closeMenuOnSelect={true}
        blurInputOnSelect={true}
        tabSelectsValue={true}
        backspaceRemovesValue={true}
        escapeClearsValue={true}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        noOptionsMessage={({ inputValue }) =>
          (inputValue || '').trim().length < 2 ? minCharsText : noResultsText
        }
        loadingMessage={() => loadingText}
        className="react-select-container"
        classNamePrefix="react-select"
        components={{
          Option: CustomOption,
          Placeholder: CustomPlaceholder,
          DropdownIndicator: () => (
            <div className="react-select__dropdown-indicator">
              <i className="bi bi-search"></i>
            </div>
          ),
          IndicatorSeparator: () => null,
        }}
        styles={{
          menu: (base) => ({
            ...base,
            zIndex: 9999
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999
          })
        }}
      />
    </div>
  )
}
